import { apiUrl } from "../utils/api";
import type { NoteRefDto } from "./vorgangService";

/**
 * Kundentagebuch — Notizen an einem Kontakt (Tab „Kundentagebuch").
 *
 * Eigene, vom Vorgangswesen getrennte Entität: eine Tagebuch-Notiz kann einem
 * Vertrag ODER einem Risiko zugewiesen sein, Gesprächspartner (intern/extern)
 * tragen, @-Erwähnungen enthalten und kommentiert werden (`parentId`).
 * Kein SWR-Cache: nach Anlegen/Ändern/Löschen wird frisch geladen.
 */

/**
 * Gesprächspartner einer Notiz — Mitarbeiter (intern) oder Kontakt (extern).
 * Beim SCHREIBEN genügt die Id; beim LESEN liefert das Backend zusätzlich ein
 * `label` für externe Partner (bzw. „Externer Kontakt", wenn der Leser den
 * Kontakt nicht sehen darf).
 */
export type Gespraechspartner =
    | { kind: "intern"; userId: string }
    | { kind: "extern"; contactId: number; label?: string };

export interface ContactNoteDto {
    id: number;
    contactId: number;
    text: string;
    /** better-auth userId des Autors — im FE via useUserLabels aufgelöst. */
    author: string;
    createdAt: string;
    updatedAt: string;
    /** @-Erwähnte (better-auth userIds). */
    mentions: string[];
    /** Kommentar an einer Notiz; null = Top-Level-Notiz. */
    parentId: number | null;
    /** Zuweisung: genau einer von beiden ist gesetzt (oder keiner = nur Kontakt). */
    contractId: number | null;
    riskObjectId: number | null;
    gespraechspartner: Gespraechspartner[];
}

/** Eingabe zum Anlegen/Ändern — Felder, die der Nutzer im Editor setzt. */
export interface ContactNoteInput {
    /**
     * Beim ÄNDERN weglassbar = Text unverändert lassen (etwa beim reinen
     * Umsortieren der Blockfläche). Beim Anlegen Pflicht.
     */
    text?: string;
    mentions?: string[];
    parentId?: number | null;
    contractId?: number | null;
    riskObjectId?: number | null;
    gespraechspartner?: Gespraechspartner[];
    /**
     * Blockfelder (PO 08.09.2026). `format: 'html'` kennzeichnet Inhalt aus dem
     * Blockeditor — ohne die Angabe rendert die Fläche das Markup beim nächsten
     * Laden als Klartext. Was der Aufruf nicht mitschickt, lässt das Backend
     * stehen (Teil-Update).
     */
    format?: "text" | "html";
    refs?: NoteRefDto[];
    sortIndex?: number | null;
    /** Gezogene Breite in Pixel; null = mit dem Inhalt wachsen. */
    breite?: number | null;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        ...options,
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`API error ${res.status}: ${text}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

export const contactNoteService = {
    /** Alle Notizen eines Kontakts inkl. Kommentare (neueste zuerst). */
    async list(contactId: number): Promise<ContactNoteDto[]> {
        return request<ContactNoteDto[]>(apiUrl(`contact/${contactId}/notes`));
    },

    /** Neue Notiz oder Kommentar (`parentId`) anlegen. */
    async create(contactId: number, input: ContactNoteInput): Promise<ContactNoteDto> {
        return request<ContactNoteDto>(apiUrl(`contact/${contactId}/notes`), {
            method: "POST",
            body: JSON.stringify(input),
        });
    },

    /** Notiz ändern (nur der Autor). */
    async update(noteId: number, input: ContactNoteInput): Promise<ContactNoteDto> {
        return request<ContactNoteDto>(apiUrl(`contact-notes/${noteId}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
    },

    /** Notiz löschen (Soft-Delete, nur der Autor). Kommentare gehen mit. */
    async remove(noteId: number): Promise<void> {
        await request<void>(apiUrl(`contact-notes/${noteId}`), {
            method: "DELETE",
        });
    },

    /**
     * Notiz in einen (zuvor angelegten) Vorgang verschieben — sie wird dort zur
     * ersten Notiz und verlässt das Tagebuch. Idempotent.
     */
    async moveToVorgang(
        noteId: number,
        vorgangId: number,
    ): Promise<{ vorgangId: number; vorgangNoteId: number }> {
        return request<{ vorgangId: number; vorgangNoteId: number }>(
            apiUrl(`contact-notes/${noteId}/to-vorgang`),
            { method: "POST", body: JSON.stringify({ vorgangId }) },
        );
    },

    /**
     * Einen Block einem Vorgang bzw. To-do zuordnen. Anders als „→ Vorgang"
     * (`moveToVorgang`) bleibt die Notiz im Tagebuch stehen — sie bekommt nur
     * einen Bezug. `vorgangId: null` löst die Zuordnung wieder.
     */
    async zuordnen(
        noteId: number,
        input: { vorgangId: number | null; todoId?: number | null },
    ): Promise<void> {
        await request<{ ok: true }>(apiUrl(`contact-notes/${noteId}/zuordnung`), {
            method: "PATCH",
            body: JSON.stringify(input),
        });
    },
};
